import React, { useEffect, useState, useCallback, useMemo } from "react";
import CollectionTile from "./components/CollectionTile";
import FilterPanel from "./components/FilterPanel";
import useApi from "@common/utils/useApi";
import { useNavigate, useParams } from "react-router-dom";
import apiUrl from "@common/utils/apiURL";

const removeDuplicates = (arr) => {
  const seen = new Set();
  return arr.filter((obj) => {
    const lowerCaseCollectionId = obj.collectionId.trim().toLowerCase();
    if (seen.has(lowerCaseCollectionId)) {
      return false;
    } else {
      seen.add(lowerCaseCollectionId);
      return true;
    }
  });
};

// Collection filter URL mappings based on actual data
const collectionUrlMappings = {
  endUse: {
    curtain: "Curtain",
    upholstery: "Upholstery",
    multipurpose: "Multipurpose",
  },
  fabtype: {
    "plains-dimout-and-blackout": "Plains, Dimout and Blackout",
    "chenille-and-boucle": "Chenille and Boucle",
    sheers: "Sheers",
    texture: "Texture",
    "embroidery-and-prints": "Embroidery and Prints",
    plains: "Plains",
    "dimout-and-blackout": "Dimout and Blackout",
    foil: "Foil",
  },
  category: {
    "room-darkening": "Room Darkening",
    "spill-safe": "Spill Safe",
    "decorative-fabrics": "Decorative Fabrics",
    "sustainable-yarns": "Sustainable Yarns",
    all: "All",
  },
};

// Default filters for collections
const defaultCollectionFilters = {
  endUse: [],
  collectionName: [],
  fabtype: [],
  category: [],
  trending: [],
  classic: [],
  priceRange: [0, 0],
};

// Helper functions for URL management
const getSlugFromFilterValue = (filterType, value) => {
  // For collectionName, just convert to slug format
  if (filterType === "collectionName") {
    return value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }

  const mappings = collectionUrlMappings[filterType];
  if (!mappings)
    return value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  const entry = Object.entries(mappings).find(
    ([, filterValue]) => filterValue === value
  );
  return entry
    ? entry[0]
    : value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
};

const getFilterValueFromSlug = (filterType, slug) => {
  const mappings = collectionUrlMappings[filterType];

  // For collectionName (handle both cases), use the slug as-is (just capitalize properly)
  if (filterType === "collectionName" || filterType.toLowerCase() === "collectionname") {
    const result = slug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return result;
  }

  // For other filter types, use mappings or fallback
  const result =
    mappings?.[slug] ||
    slug.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  return result;
};

function Collection() {
  const navigate = useNavigate();
  const { filterType, filterValue } = useParams();
  const [collectionListingData, setCollectionListingData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(
    defaultCollectionFilters
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

  // Debug URL params and reset initialization when URL changes
  useEffect(() => {
    setHasInitializedFilters(false);
  }, [filterType, filterValue]);

  // Initialize filters from URL parameters - wait for both data and URL params
  useEffect(() => {
    // Only initialize if we have data and haven't initialized yet
    if (!hasInitializedFilters && collectionListingData.length > 0) {
      let initialFilters = { ...defaultCollectionFilters };

      // Check URL params first (URL structure takes priority)
      if (filterType && filterValue) {
        const decodedFilterValue = decodeURIComponent(filterValue);

        // Normalize filter type to handle case variations
        let normalizedFilterType = filterType;
        const filterTypeMap = {
          'collectionname': 'collectionName',
          'fabtype': 'fabtype',
          'enduse': 'endUse',
          'category': 'category',
          'trending': 'trending',
          'classic': 'classic'
        };

        if (filterTypeMap[filterType.toLowerCase()]) {
          normalizedFilterType = filterTypeMap[filterType.toLowerCase()];
        }

        const actualFilterValue = getFilterValueFromSlug(
          normalizedFilterType,
          decodedFilterValue
        );

        console.log("URL Filter Processing:", {
          originalFilterType: filterType,
          normalizedFilterType,
          filterValue,
          decodedFilterValue,
          actualFilterValue,
        });

        if (initialFilters.hasOwnProperty(normalizedFilterType)) {
          initialFilters[normalizedFilterType] = [actualFilterValue];
          console.log("URL Filters initialized:", {
            normalizedFilterType,
            actualFilterValue,
            initialFilters,
          });
        } else {
          console.warn("Filter type not found:", normalizedFilterType, "Available:", Object.keys(initialFilters));
        }
      }
      // If no URL params, try to restore from localStorage
      else {
        try {
          const savedFilters = localStorage.getItem("collectionFilters");
          if (savedFilters) {
            const parsedFilters = JSON.parse(savedFilters);
            initialFilters = { ...defaultCollectionFilters, ...parsedFilters };
            console.log("LocalStorage Filters restored:", initialFilters);
          }
        } catch (error) {
          console.error("Error parsing saved collection filters:", error);
          localStorage.removeItem("collectionFilters");
        }
      }

      setSelectedFilters(initialFilters);

      if (window.innerWidth > 1024 && filterType) {
        setFilterOpen(true);
      }

      setHasInitializedFilters(true);
    }
  }, [
    filterType,
    filterValue,
    hasInitializedFilters,
    collectionListingData.length,
  ]);

  const getAllCollectionURL = apiUrl.collection.getAll;

  const { data: response, loading: isLoading } = useApi(getAllCollectionURL);
  useEffect(() => {
    if (!isLoading && response) {
      const uniqueCollectionGallery = removeDuplicates(response);
      console.log("Collection data loaded:", uniqueCollectionGallery);

      // Debug: Show available collection names
      const collectionNames = uniqueCollectionGallery
        .map((item) => item.name)
        .filter((name) => name)
        .sort();
      console.log("Available collection names:", collectionNames);

      setCollectionListingData(uniqueCollectionGallery);
    }
  }, [isLoading, response]);

  const filteredCollectionData = useMemo(() => {
    // Check if any filters are applied
    const hasFilters = Object.entries(selectedFilters).some(([key, value]) => {
      if (key === "priceRange") {
        return value[1] !== 0;
      }
      return Array.isArray(value) && value.length > 0;
    });

    console.log("Filter Debug:", {
      selectedFilters,
      hasFilters,
      collectionDataLength: collectionListingData.length,
      filterType,
      filterValue,
    });

    const sortByLunchDate = (arr) =>
      [...arr].sort((a, b) => {
        const dateA = a.launchDate ? new Date(a.launchDate) : new Date(0);
        const dateB = b.launchDate ? new Date(b.launchDate) : new Date(0);
        return dateB - dateA;
      });

    if (!hasFilters) {
      return collectionListingData.filter((item) => item.name !== null); // Filter out items with null names
    }

    let filtered = collectionListingData.filter((item) => {
      // Skip items with null names
      if (!item.name) return false;

      // Handle endUse (single value, not comma-separated in the data)
      const isEndUseMatch =
        selectedFilters.endUse.length === 0 ||
        selectedFilters.endUse.includes(item.endUse);

      // Handle collectionName (case-insensitive matching)
      const isCollectionNameMatch =
        selectedFilters.collectionName.length === 0 ||
        selectedFilters.collectionName.some(filterName =>
          filterName.toLowerCase() === item.name.toLowerCase()
        );

      // Handle fabtype (can be comma-separated)
      const itemFabTypes = item.fabType
        ? item.fabType.split(",").map((e) => e.trim())
        : [];
      const isFabTypeMatch =
        selectedFilters.fabtype.length === 0 ||
        selectedFilters.fabtype.some((fabType) =>
          itemFabTypes.includes(fabType)
        );

      // Handle category (can be comma-separated)
      const itemCategories = item.category
        ? item.category.split(",").map((e) => e.trim())
        : [];
      const isCategoryMatch =
        selectedFilters.category.length === 0 ||
        selectedFilters.category.some((category) =>
          itemCategories.includes(category)
        );

      // Handle trending (check if trending value > 0)
      const isTrendingMatch =
        selectedFilters.trending.length === 0 ||
        (item.trending !== null && item.trending > 0);

      // Handle classic (check if classic value > 0)
      const isClassicMatch =
        selectedFilters.classic.length === 0 ||
        (item.classic !== null && item.classic > 0);

      // Handle price range
      const isPriceMatch =
        selectedFilters.priceRange[1] === 0 ||
        (item.minPrice !== null &&
          item.maxPrice !== null &&
          item.minPrice >= selectedFilters.priceRange[0] &&
          item.maxPrice <= selectedFilters.priceRange[1]);

      return (
        isEndUseMatch &&
        isCollectionNameMatch &&
        isFabTypeMatch &&
        isCategoryMatch &&
        isPriceMatch &&
        isTrendingMatch &&
        isClassicMatch
      );
    });

    // Apply sorting
    if (selectedFilters.trending.length > 0) {
      filtered.sort((a, b) => (b.trending || 0) - (a.trending || 0));
    }
    if (selectedFilters.classic.length > 0) {
      filtered.sort((a, b) => (b.classic || 0) - (a.classic || 0));
    }
    return sortByLunchDate(filtered);
  }, [selectedFilters, collectionListingData, filterType, filterValue]);

  const filterOpenHandleChange = useCallback(() => {
    setFilterOpen(true);
  }, []);

  const filterCloseHandleChange = useCallback(() => {
    setFilterOpen(false);
  }, []);

  const applySelectedFilters = useCallback(
    (filters) => {
      setSelectedFilters(filters);

      // Save to localStorage
      localStorage.setItem("collectionFilters", JSON.stringify(filters));

      // Update URL based on applied filters - prioritize non-price filters
      const activeFilters = Object.entries(filters).filter(([key, value]) => {
        if (key === "priceRange") return value[1] !== 0;
        return Array.isArray(value) && value.length > 0;
      });

      // Find the first non-price filter for URL
      const urlFilter = activeFilters.find(([key]) => key !== "priceRange");

      if (urlFilter) {
        const [filterType, filterValues] = urlFilter;
        if (Array.isArray(filterValues) && filterValues.length > 0) {
          const slug = getSlugFromFilterValue(filterType, filterValues[0]);
          // Use lowercase for URL but keep the original filterType structure
          const urlFilterTypeMap = {
            'collectionName': 'collectionname',
            'fabtype': 'fabtype',
            'endUse': 'enduse',
            'category': 'category',
            'trending': 'trending',
            'classic': 'classic'
          };
          const urlFilterType = urlFilterTypeMap[filterType] || filterType;
          navigate(`/collections/${urlFilterType}/${slug}`.toLowerCase(), {
            replace: true,
          });
        }
      } else if (activeFilters.length === 0) {
        navigate("/collections".toLowerCase(), { replace: true });
      }
      // If only price filter is active, stay on current URL
    },
    [navigate]
  );

  const resetFilters = useCallback(() => {
    setSelectedFilters(defaultCollectionFilters);
    localStorage.removeItem("collectionFilters");
    navigate("/collections".toLowerCase(), { replace: true });
  }, [navigate]);

  // Memoize preSelectedFilters to prevent unnecessary FilterPanel re-renders
  const memoizedSelectedFilters = useMemo(
    () => selectedFilters,
    [selectedFilters]
  );

  return (
    <div className="bs-section">
      <div className="bs-section__section-head bs-section--typ-collections">
        <h2 className="bs-section__title">
          <button
            onClick={resetFilters}
            className="icon icon-o-left lyt-product-list__back-link"
            style={{
              marginRight: "10px",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          ></button>
          <span>collections</span>
        </h2>
      </div>
      <div className="bs-section__section-cont">
        <div
          className={`lyt-collection ${
            filterOpen ? "lyt-collection__filter-open" : ""
            }`}
        >
          {/* onClick={filterOpenHandleChange} */}
          {!filterOpen ? (
            <button
              className="bs-btn bs-btn__btn-icon-link lyt-collection__filter-action"
              onClick={filterOpenHandleChange}
            >
              <span className="bs-btn__icon icon icon-filter"></span>
              <span className="bs-btn__btn-text">filter</span>
            </button>
          ) : null}
          {window.innerWidth <= 1024 ? (
            <FilterPanel
              filterCloseHandleChange={filterCloseHandleChange}
              classname={filterOpen ? "bs-filter__mb-active" : ""}
              collectionData={collectionListingData}
              applySelectedFilters={applySelectedFilters}
              preSelectedFilters={memoizedSelectedFilters}
            />
          ) : (
            filterOpen && (
              <div className="lyt-collection__filter-wrap">
                <FilterPanel
                  filterCloseHandleChange={filterCloseHandleChange}
                  collectionData={collectionListingData}
                  applySelectedFilters={applySelectedFilters}
                  preSelectedFilters={memoizedSelectedFilters}
                />
              </div>
            )
          )}

          <ul className="lyt-collection__list-wrap">
            {filteredCollectionData.map((item, index) => {
              return (
                <li
                  className="lyt-collection__item"
                  key={item.collectionId || index}
                >
                  <CollectionTile data={item} />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Collection;
